import type { ClientMessage, Credentials, QuizView, ServerMessage } from '@elixir/shared';
import { Store } from './store';

export interface QuizSocketState {
  quiz: QuizView | null;
  connection: 'connecting' | 'open' | 'retrying' | 'closed';
  /** Set when we must not reconnect (removed, or session invalid). */
  fatal: string | null;
  lastError: string | null;
}

/**
 * Live quiz state (teams, scores, who's online) from the Elixir server.
 * Reconnects with backoff; every connect receives a full snapshot, so nothing is lost on refresh.
 */
export class QuizSocket {
  readonly store = new Store<QuizSocketState>({ quiz: null, connection: 'connecting', fatal: null, lastError: null });
  private ws: WebSocket | null = null;
  private attempts = 0;
  private retryTimer: number | null = null;
  private pingTimer: number | null = null;
  private closed = false;
  /** Messages that matter to the voice layer. */
  onForceMute?: () => void;
  onKicked?: () => void;

  constructor(private creds: Credentials) {
    this.connect();
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close() {
    this.closed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close();
    this.store.set({ connection: 'closed' });
  }

  clearError() {
    this.store.set({ lastError: null });
  }

  private connect() {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const q = new URLSearchParams({ code: this.creds.code, pid: this.creds.participantId, secret: this.creds.secret });
    const ws = new WebSocket(`${proto}//${location.host}/ws?${q}`);
    this.ws = ws;

    ws.onopen = () => {
      this.attempts = 0;
      this.store.set({ connection: 'open' });
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
    };

    ws.onmessage = (e) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      switch (msg.type) {
        case 'state': {
          const current = this.store.get().quiz;
          if (!current || msg.quiz.version >= current.version || msg.quiz.code !== current.code) {
            this.store.set({ quiz: msg.quiz });
          }
          break;
        }
        case 'forceMute':
          this.onForceMute?.();
          break;
        case 'kicked':
          this.store.set({ fatal: 'The quizmaster removed you from this quiz.' });
          this.onKicked?.();
          break;
        case 'error':
          this.store.set({ lastError: msg.message });
          break;
      }
    };

    ws.onclose = (e) => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      if (this.closed || this.store.get().fatal) return;
      if (e.code === 4003) {
        this.store.set({ fatal: 'The quizmaster removed you from this quiz.', connection: 'closed' });
        return;
      }
      // Bad sessions are caught when the voice connection asks for tokens; here we just keep retrying.
      const delay = Math.min(10_000, 500 * 2 ** this.attempts++);
      this.store.set({ connection: 'retrying' });
      this.retryTimer = window.setTimeout(() => this.connect(), delay);
    };
  }
}
